import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CourseLessonEntity, QuizQuestionLinkEntity, QuizResultQuestionEntity } from '../../../../meta-data/app-meta-data.service';
import { DwMediaRepositoryService, isFirstChange } from '@devwareapps/devware-cap';

@Component({
  selector: 'app-quiz-question',
  templateUrl: './quiz-question.component.html',
  styleUrls: ['./quiz-question.component.scss']
})
export class QuizQuestionComponent implements OnInit, OnChanges {

  @Input() questionLink: QuizQuestionLinkEntity;
  @Input() lesson: CourseLessonEntity;
  @Input() questionResult: QuizResultQuestionEntity;
  @Input() reviewAnswers: boolean = false;
  @Input() testMode: boolean = false;
  @Output() questionResultChange = new EventEmitter<QuizResultQuestionEntity>();

  questionImageUrl: string = '';
  answerImageUrl: string = '';
  answerAttachmentUrl: string = '';
  referencePdfUrl: string = '';
  referencePdfLabel: string = '';
  private referencePdfMediaId: number | null = null;
  private correctAnswerMediaId: number | null = null;

  constructor(private dwMediaRepository: DwMediaRepositoryService,) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isFirstChange(changes)) {
      this.setupQuestion();
    }
  }

  ngOnInit(): void {
    this.setupQuestion();
  }

  setupQuestion() {

    this.questionImageUrl = '';
    if (this.questionLink?.Question?.QuestionImageDwroMedia?.MediaPath) {
      this.questionImageUrl = `${this.dwMediaRepository.mediaApi}/download/${this.questionLink.Question.QuestionImageDwroMedia.MediaPath}`;
    }

    this.setupCorrectAnswerMedia();
    this.setupReferencePdf();
  }

  answerChanged(questionResult: QuizResultQuestionEntity): void {
    this.questionResult = questionResult;
    this.questionResultChange.emit(questionResult);
  }

  private setupCorrectAnswerMedia() {
    const prefetchedMediaPath = this.questionLink?.Question?.CorrectAnswerImageDwroMedia?.MediaPath;
    if (prefetchedMediaPath) {
      this.applyCorrectAnswerMediaPath(prefetchedMediaPath);
      this.correctAnswerMediaId = null;
      return;
    }

    const mediaId = this.questionLink?.Question?.CorrectAnswerImageMediaId;
    if (!mediaId) {
      this.correctAnswerMediaId = null;
      this.answerImageUrl = '';
      this.answerAttachmentUrl = '';
      return;
    }

    if (this.correctAnswerMediaId === mediaId && (this.answerImageUrl || this.answerAttachmentUrl)) {
      return;
    }

    this.correctAnswerMediaId = mediaId;
    this.answerImageUrl = '';
    this.answerAttachmentUrl = '';

    this.dwMediaRepository.loadMedia(mediaId)
      .subscribe(media => {
        if (this.correctAnswerMediaId !== mediaId) {
          return;
        }

        const mediaPath = media?.[0]?.MediaPath;
        if (mediaPath) {
          this.applyCorrectAnswerMediaPath(mediaPath);
        }
      });
  }

  private applyCorrectAnswerMediaPath(mediaPath: string) {
    const mediaUrl = this.dwMediaRepository.getImageUrlFromMediaPath(mediaPath);

    if (this.isPdfMediaPath(mediaPath)) {
      this.answerAttachmentUrl = mediaUrl;
      this.answerImageUrl = '';
    } else {
      this.answerImageUrl = mediaUrl;
      this.answerAttachmentUrl = '';
    }
  }

  get answerAttachmentLabel(): string {
    return this.questionLink?.Question?.CorrectAnswerNotes || 'View reference PDF';
  }

  private setupReferencePdf() {
    const mediaId = this.lesson?.LessonReferebcePdfMediaId;
    this.referencePdfLabel = this.lesson?.LessonReferenceNotes || '';

    if (!mediaId) {
      this.referencePdfMediaId = null;
      this.referencePdfUrl = '';
      return;
    }

    if (this.referencePdfMediaId === mediaId && this.referencePdfUrl) {
      return;
    }

    this.referencePdfMediaId = mediaId;
    this.referencePdfUrl = '';

    this.dwMediaRepository.loadMedia(mediaId)
      .subscribe(media => {
        if (this.referencePdfMediaId !== mediaId) {
          return;
        }

        const mediaPath = media?.[0]?.MediaPath;
        this.referencePdfUrl = mediaPath ? this.dwMediaRepository.getImageUrlFromMediaPath(mediaPath) : '';
      });
  }

  private isPdfMediaPath(mediaPath: string): boolean {
    return mediaPath?.toLowerCase().split('?')[0].endsWith('.pdf');
  }

}
